
import m from "mithril";
import { fetchWithAutoRefresh } from "../utils.js";

const SubunitBackup = {
    isLoading: false,
    history: [],
    error: null,

    oninit: async () => {
        await SubunitBackup.loadHistory();
    },

    loadHistory: async () => {
        try {
            const response = await fetchWithAutoRefresh('/api/subunit-backup/history');
            if (response.ok) {
                SubunitBackup.history = await response.json();
            }
        } catch (e) {
            console.error('Failed to load history', e);
        }
        m.redraw();
    },

    downloadBackup: async () => {
        SubunitBackup.isLoading = true;
        SubunitBackup.error = null;
        m.redraw();

        try {
            const response = await fetchWithAutoRefresh('/api/subunit-backup/download');

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.error || 'Download failed');
            }

            // Create blob and download
            const blob = await response.blob();
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.style.display = 'none';
            a.href = url;

            // Get filename from header
            const disposition = response.headers.get('Content-Disposition');
            let filename = 'backup.zip';
            if (disposition && disposition.indexOf('attachment') !== -1) {
                const filenameRegex = /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/;
                const matches = filenameRegex.exec(disposition);
                if (matches != null && matches[1]) {
                    filename = matches[1].replace(/['"]/g, '');
                }
            }

            a.download = filename;
            document.body.appendChild(a);
            a.click();

            window.URL.revokeObjectURL(url);
            document.body.removeChild(a);

            // Reload history to show the new backup
            setTimeout(() => SubunitBackup.loadHistory(), 2000);

        } catch (error) {
            console.error('Backup download error:', error);
            SubunitBackup.error = error.message;
        } finally {
            SubunitBackup.isLoading = false;
            m.redraw();
        }
    },

    view: () => {
        return m("div", { class: "p-6" }, [
            m("h1", { class: "text-2xl font-bold mb-6 text-gray-800 dark:text-gray-200" }, "Backup Data Unit"),

            // Main Backup Card
            m("div", { class: "bg-white dark:bg-gray-800 rounded-lg shadow p-6 max-w-4xl mb-8" }, [
                m("div", { class: "flex flex-col md:flex-row gap-6" }, [

                    // Left Side: Description & Button
                    m("div", { class: "flex-1" }, [
                        m("h2", { class: "text-lg font-semibold mb-2 text-gray-800 dark:text-white" }, "Backup Data Sekarang"),
                        m("p", { class: "text-gray-600 dark:text-gray-300 mb-4 text-sm" },
                            "Unduh arsip (ZIP) berisi data lengkap unit Anda dalam format CSV. Data mencakup:"
                        ),
                        m("ul", { class: "list-disc list-inside text-sm text-gray-600 dark:text-gray-300 mb-6 space-y-1" }, [
                            m("li", "Daftar Nasabah (Nama, No Reg, ID, Alamat)"),
                            m("li", "Produk Simpanan & Saldo per Nasabah"),
                            m("li", "Produk Pinjaman & Status Pembayaran per Nasabah")
                        ]),

                        SubunitBackup.error && m("div", { class: "mb-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded text-sm" }, [
                            m("p", `Error: ${SubunitBackup.error}`)
                        ]),

                        m("button", {
                            class: `flex items-center justify-center w-full md:w-auto px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-lg transition duration-150 ease-in-out ${SubunitBackup.isLoading ? 'opacity-70 cursor-not-allowed' : ''}`,
                            onclick: SubunitBackup.downloadBackup,
                            disabled: SubunitBackup.isLoading
                        }, [
                            SubunitBackup.isLoading
                                ? [m("i", { class: "ri-loader-4-line animate-spin mr-2" }), "Memproses..."]
                                : [m("i", { class: "ri-download-cloud-2-line mr-2" }), "Download Backup (.zip)"]
                        ])
                    ]),

                    // Right Side: Recent History
                    m("div", { class: "flex-1 border-t md:border-t-0 md:border-l border-gray-200 dark:border-gray-700 pt-6 md:pt-0 md:pl-6" }, [
                        m("h3", { class: "text-sm font-semibold uppercase text-gray-500 dark:text-gray-400 mb-4" }, "Riwayat Backup (7 Hari Terakhir)"),

                        SubunitBackup.history.length === 0
                            ? m("p", { class: "text-gray-500 italic text-sm" }, "Belum ada backup dalam 7 hari terakhir.")
                            : m("div", { class: "space-y-3" }, SubunitBackup.history.map(b => {
                                // backup_date is a UTC instant (MySQL NOW() on UTC server,
                                // serialized with Z) — format it in WIB for display.
                                const date = new Date(b.backup_date);

                                // Format date part: Sabtu, 31 Okt 2026 (WIB)
                                const dateStr = date.toLocaleDateString('id-ID', {
                                    weekday: 'long',
                                    year: 'numeric',
                                    month: 'short',
                                    day: 'numeric',
                                    timeZone: 'Asia/Jakarta'
                                });

                                // Format time part: 09.37.47 (WIB)
                                const timeStr = date.toLocaleTimeString('id-ID', {
                                    hour: '2-digit',
                                    minute: '2-digit',
                                    second: '2-digit',
                                    hour12: false,
                                    timeZone: 'Asia/Jakarta'
                                }).replace(/:/g, '.'); // Force dots if locale doesn't provide them

                                return m("div", { class: "flex items-center justify-between text-sm" }, [
                                    m("div", [
                                        m("p", { class: "font-medium text-gray-800 dark:text-gray-200" }, dateStr),
                                        m("p", { class: "text-xs text-gray-500" }, `${timeStr} • oleh ${b.username || 'System'}`)
                                    ]),
                                    m("span", { class: "text-xs px-2 py-1 bg-green-100 text-green-800 rounded-full" }, "Success")
                                ]);
                            }))
                    ])
                ])
            ])
        ]);
    }
};

export default SubunitBackup;
